import React from 'react';
import {
  useOrgMembers,
  usePlan,
  useOrgInvitations,
  useOrgLicense,
  useCreateInvitation,
  formatDate,
} from '@redbax/shared';
import type { Member, Invitation, Exclusion } from '@redbax/shared';
import {
  PageHeader,
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
  UpgradePrompt,
  DataTable,
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Input,
  Label,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  LoadingSkeleton,
  type Column,
} from '@redbax/ui-kit';
import { Plus } from 'lucide-react';

const memberColumns: Column<Member>[] = [
  { key: 'displayName', header: 'Name', sortable: true },
  { key: 'email', header: 'Email', sortable: true },
  {
    key: 'role',
    header: 'Role',
    render: (row) => <Badge variant="outline">{row.role}</Badge>,
  },
  {
    key: 'joinedAt',
    header: 'Joined',
    render: (row) => formatDate(row.joinedAt),
  },
];

const invitationColumns: Column<Invitation>[] = [
  { key: 'email', header: 'Email', sortable: true },
  { key: 'role', header: 'Role' },
  {
    key: 'status',
    header: 'Status',
    render: (row) => (
      <Badge variant={row.status === 'Pending' ? 'warning' : row.status === 'Accepted' ? 'success' : 'secondary'}>
        {row.status}
      </Badge>
    ),
  },
  {
    key: 'expiresAt',
    header: 'Expires',
    render: (row) => formatDate(row.expiresAt),
  },
];

export function SettingsPage() {
  const { data: members, isLoading: membersLoading } = useOrgMembers();
  const { data: invitations, isLoading: invitationsLoading } = useOrgInvitations();
  const { data: license, isLoading: licenseLoading } = useOrgLicense();
  const createInvitation = useCreateInvitation();
  const { isPersonal } = usePlan();

  const [inviteEmail, setInviteEmail] = React.useState('');
  const [inviteRole, setInviteRole] = React.useState('Viewer');

  const handleInvite = () => {
    if (!inviteEmail.trim()) return;
    createInvitation.mutate({ email: inviteEmail, role: inviteRole });
    setInviteEmail('');
  };

  return (
    <div>
      <PageHeader title="Settings" description="Organization settings and management" />

      <Tabs defaultValue="members">
        <TabsList>
          <TabsTrigger value="members">Members</TabsTrigger>
          {!isPersonal && <TabsTrigger value="invitations">Invitations</TabsTrigger>}
          <TabsTrigger value="license">License</TabsTrigger>
        </TabsList>

        <TabsContent value="members">
          {/* "INVITE YOUR TEAM" (story #135). Shown where the roster would be, because that is where
              someone notices they are working alone - and a personal organization is one person by
              definition (ADR-014), which CreateInvitationHandler enforces regardless of this. */}
          {isPersonal && (
            <UpgradePrompt
              className="mt-4"
              surface="invite_team"
              title="Working with other people?"
              description={
                'Free accounts cover one person. Upgrading to a team lets you invite colleagues, ' +
                'share what has been detected across everyone\'s machines, and set your own policy.'
              }
            />
          )}

          <Card className="mt-4">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-base">Team Members</CardTitle>
                {/* Hidden rather than disabled: an invite box a free user can type into and never send
                    is worse than not offering it, and the upsell above says why it is absent. */}
                <div className={isPersonal ? 'hidden' : 'flex items-center gap-2'}>
                  <Input
                    placeholder="Email address"
                    value={inviteEmail}
                    onChange={(e) => setInviteEmail(e.target.value)}
                    className="w-60"
                  />
                  <Select value={inviteRole} onValueChange={setInviteRole}>
                    <SelectTrigger className="w-28">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Admin">Admin</SelectItem>
                      <SelectItem value="Analyst">Analyst</SelectItem>
                      <SelectItem value="Viewer">Viewer</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button size="sm" onClick={handleInvite} disabled={!inviteEmail.trim()}>
                    <Plus className="mr-1 h-4 w-4" />
                    Invite
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <DataTable
                columns={memberColumns}
                data={members ?? []}
                keyExtractor={(m) => m.userId}
                loading={membersLoading}
                emptyMessage="No members yet"
              />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="invitations">
          <Card className="mt-4">
            <CardHeader>
              <CardTitle className="text-base">Pending Invitations</CardTitle>
            </CardHeader>
            <CardContent>
              <DataTable
                columns={invitationColumns}
                data={invitations ?? []}
                keyExtractor={(i) => i.id}
                loading={invitationsLoading}
                emptyMessage="No pending invitations"
              />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="license">
          {/* The one upsell nobody was pushed into (story #135). Separate from the boundary prompts on
              purpose: an upgrade started here says the product convinced them, while one started at the
              device cap says the limit did. Distinguishing the two is most of what story #137 is for. */}
          {isPersonal && (
            <UpgradePrompt
              className="mt-4"
              surface="plan_settings"
              title="You're on the free plan"
              description={
                'Two devices, seven days of event history, and Redbax\'s default detection policy. ' +
                'Upgrading to a team adds unlimited devices, full history, your own policy and ' +
                'colleagues — and your devices keep working throughout.'
              }
            />
          )}

          <Card className="mt-4">
            <CardHeader>
              <CardTitle className="text-base">License Information</CardTitle>
            </CardHeader>
            <CardContent>
              {licenseLoading ? (
                <LoadingSkeleton rows={4} />
              ) : license ? (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-text-secondary">Tier</Label>
                    <p className="mt-1 text-text-primary">{license.tier}</p>
                  </div>
                  <div>
                    <Label className="text-text-secondary">Seats</Label>
                    <p className="mt-1 text-text-primary">{license.seatCount}</p>
                  </div>
                  <div>
                    <Label className="text-text-secondary">Started</Label>
                    <p className="mt-1 text-text-primary">
                      {formatDate(license.startsAt)}
                    </p>
                  </div>
                  <div>
                    <Label className="text-text-secondary">Expires</Label>
                    <p className="mt-1 text-text-primary">
                      {license.expiresAt
                        ? formatDate(license.expiresAt)
                        : 'Never'}
                    </p>
                  </div>
                </div>
              ) : (
                <p className="text-sm text-text-secondary">No license information available</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
